import englishMessages from 'ra-language-english';
import { TranslationMessages } from 'react-admin';

export const en: TranslationMessages = {
  ...englishMessages,
  custom: {
    auth: {
      login: {
        title: 'Login',
        subtitle: 'Please enter your email and password to login',
        email: 'Email',
        password: 'Password',
        submit: 'Login',
        forgot_password: 'Forgot password?',
        error: 'Invalid email or password',
      },
      forgot_password: {
        title: 'Forgot password',
        subtitle: 'Please enter your email to reset your password',
        email: 'Email',
        submit: 'Reset password',
        back_to_login: 'Back to login',
        error: 'Invalid email',
        success: 'Password reset instructions sent successfully',
        failed: 'Error sending password reset instructions',
      },
      reset_password: {
        title: 'Reset password',
        subtitle: 'Please enter your new password',
        password: 'New password',
        confirm_password: 'Confirm password',
        submit: 'Reset password',
        back_to_login: 'Back to login',
        error: 'Passwords do not match',
        success: 'Password reset successfully',
        failed: 'Error resetting password',
        passwords: {
          token: 'Invalid token',
          email: 'Email',
        },
        validation: {
          confirmed: 'Passwords do not match',
        },
      },
      verify_email: {
        title: 'Verify email',
        subtitle: 'Please enter the code sent to your email',
        code: 'Code',
        submit: 'Verify email',
        back_to_login: 'Back to login',
        error: 'Invalid code',
      },
    },
    actions: {
      add: 'Add %{name}',
      copy: 'Copy to clipboard',
      preview: 'Preview',
      download: 'Download',
      edit: 'Edit %{name}',
      request: 'Request',
      show: 'Show %{name}',
    },
    labels: {
      actions: 'Actions',
      compatible: 'Compatible',
      icon: 'Icon',
      options: 'Options',
      required: 'Required',
      universal: 'Universal',
      used_by: 'Used by',
    },
    notification: {
      error: {
        not_found: '%{item} not found',
      },
      success: '%{action} successful',
      download: '%{item} downloaded',
      download_started: '%{item} download started',
    },
  },
  message: {
    add_title: 'Add %{resource} to %{target}.',
  },
  validation: {
    error: 'Validation error',
    exists: 'Item does not exist',
    required: 'Required',
    unique: 'Item already exists',
  },
  resources: {
    activity_logs: {
      fields: {
        created_at: 'Activity at',
      },
      expand: {
        label: 'Changed properties',
      },
    },
    app_collection_defaults: {
      fields: {
        app_collection_id: 'Collection',
        created_at: 'Date added',
      },
    },
    app_collection_subscriptions: {
      name: 'Subscription |||| Subscriptions',
      fields: {
        app_collection_id: 'Collection',
        length: 'Duration',
        my_tover_access: 'My Tover access',
        start_date: 'Start date',
        start_now: 'Start now',
        invoice_number: 'Invoice number',
      },
    },
    app_collection_trials: {
      name: 'Trial collection |||| Trial collections',
      forcedCaseName: 'Trial collection |||| Trial collections',
      fields: {
        active: 'Active',
        app_collection_id: 'Collection',
        configuration_id: 'Configuration ID',
        created_at: 'Created at',
        length: 'Duration',
        valid_from: 'Valid from',
        valid_until: 'Valid until',
      },
    },
    app_collections: {
      name: 'Collection |||| Collections',
    },
    app_trials: {
      name: 'Trial app |||| Trial apps',
      fields: {
        created_at: 'Date added',
        length: 'Duration',
        translated_name: 'Name',
      },
    },
    apps: {
      name: 'App |||| Apps',
      forcedCaseName: 'App |||| Apps',
      fields: {
        'app_ids': 'Apps',
        'icon.src': 'Icon',
        'cognitive': 'Game Level',
        'created_at': 'Date added',
        'flavour_ids': 'Target groups',
        'designation_ids': 'Designations',
        'lab_enabled': 'Tover Lab enabled',
        'icon': 'Icon',
        'name': 'Game code',
        'product_id': 'Product ID',
        'translated_name': 'Name',
      },
    },
    collections: {
      name: 'Collection |||| Collections',
      forcedCaseName: 'Collection |||| Collections',
      fields: {
        app_id: 'App',
        app_ids: 'Apps',
        apps_count: 'Apps',
        available_as_subscription: 'Available as subscription?',
        designation_ids: 'Designations',
        flavour_id: 'Target group',
        name: 'Name',
      },
      tabs: {
        general: 'General',
        apps: 'Apps',
      },
    },
    commands: {
      fields: {
        status: 'Status',
      },
      status: {
        active: 'Active',
        deleted: 'Deleted',
        deleted_at: 'Deleted on %{date}',
      },
      restore: {
        action: 'Restore',
        success: 'Command "%{name}" has been restored',
        error: 'Could not restore command "%{name}". %{error}',
        deleted_notice: 'This command was deleted on %{date}. Restore it to make it available again.',
      },
      create: {
        created: 'Command "%{name}" has been created',
        created_with_parameters: 'Command "%{name}" has been created with %{count} parameter(s)',
        parameters_partially_saved:
          'Command "%{name}" was created, but not all of its parameters could be saved. Saved: %{saved}. Not saved: %{failed}. Add the missing parameters here.',
        none: 'none',
        name_taken_by_deleted_title: 'This name belongs to a deleted command',
        name_taken_by_deleted:
          'A deleted command named "%{name}" still exists. Restore that command instead of creating a new one, or choose a different name.',
      },
      edit: {
        save_failed: 'The command could not be saved',
      },
      parameters: {
        title: 'Parameters',
        add: 'Add parameter',
        empty: 'No parameters defined',
        draft_hint: 'Parameters are saved right after the command is created, in the order shown here.',
        move_up: 'Move up',
        move_down: 'Move down',
        duplicate_name: 'A parameter named "%{name}" is already defined for this command',
        fields: {
          name: 'Name',
          display_name: 'Display name',
          data_type: 'Data type',
          ui_component: 'UI component',
          required: 'Required',
          order: 'Order',
          actions: 'Actions',
        },
      },
    },
    configurations: {
      fields: {
        id: 'Configuration ID',
        serial: 'Serial number',
      },
    },
    countries: {
      name: 'Country |||| Countries',
      fields: {
        active: 'Show active only',
        name: 'Name',
      },
      tabs: {
        general: 'General',
        regions: 'Regions',
      },
    },
    designations: {
      name: 'Designation |||| Designations',
      forcedCaseName: 'Designation |||| Designations',
      fields: {
        app_collection_ids: 'Collections',
        app_ids: 'Apps',
        default: 'Default',
        designation_ids: 'Designations',
        name: 'Name',
      },
      tabs: {
        apps: 'Apps',
        collections: 'Collections',
        general: 'General',
      },
    },
    diagnostics: {
      fields: {
        attachments: 'Attachments',
        created_at: 'Date added',
        data: {
          script: 'Name',
        },
        identifier: 'Identifier',
        type: 'Type',
      },
    },
    flavours: {
      name: 'Target Group |||| Target Groups',
      forcedCaseName: 'Target Group |||| Target Groups',
      fields: {
        app_collection_id: 'Collection',
        color: 'Color',
        default_app_collection_id: 'Default collection',
        default_app_collections: 'Default collections',
        locale_id: 'Language',
        name: 'Name',
        serial: 'Serial number',
      },
    },
    hardware: {
      fields: {
        name: 'Name',
        flavour_id: 'Default flavour',
      },
      section: {
        general: 'Hardware',
        hardware_revisions: 'Revisions',
        software_components: 'Software Components',
      },
    },
    hardware_revisions: {
      name: 'Hardware revision |||| Hardware revisions',
      fields: {
        update_channel_id: 'Default update channel',
        update_channel_ids: 'Update channels',
      },
    },
    locales: {
      name: 'Locale |||| Locales',
      forcedCaseName: 'Locale |||| Locales',
      fields: {
        description: 'Description',
        language_ids: 'Additional languages',
        name: 'Name',
      },
    },
    partners: {
      fields: {
        sales_force_id: 'Salesforce ID',
        client_number: 'Partner ID',
        service_person: 'Contact person',
        service_phone_number: 'Contact phone number',
        name: 'Name',
        sales_email: 'Sales email',
        service_email: 'Service email',
      },
      section: {
        general: 'General',
        partner_groups: 'Partner groups',
      },
      name: 'Partner |||| Partners',
    },
    partner_groups: {
      name: 'Partner group |||| Partner groups',
      forcedCaseName: 'Partner group |||| Partner groups',
      empty: "This partner doesn't belong to any partner groups.",
      fields: {
        name: 'Name',
        account_manager: 'Account manager',
        service_email: 'Service email',
        country_id: 'Country',
        region_id: 'Region',
      },
      message: {
        create_content: 'Please provide a name to the newly created region for this partner group.',
        add_content: 'Add a new region for this partner group.',
        update_content: "Update details of this partner group's information",
      },
    },
    playlists: {
      fields: {
        icon: 'Icon',
      },
    },
    regions: {
      name: 'Region |||| Regions',
      fields: {
        name: 'Name',
      },
      empty: 'There are no regions to this country',
    },
    software_versions: {
      name: 'Software version |||| Software versions',
      fields: {
        version: 'Version',
        hardware_ids: 'Hardware',
        software_component_id: 'Software component',
        update_channel_ids: 'Update channels',
      },
    },
    software_components: {
      name: 'Software component |||| Software components',
      fields: {
        name: 'Name',
        hardware_ids: 'Hardware',
        path: 'Path',
        build_project_name: 'Build project name',
        required_hardware_ids: 'Required hardware',
      },
    },
    software_releases: {
      name: 'Software release |||| Software releases',
      create: {
        title: 'Create release',
        clone_read_only: 'clone: read only',
        software_components: 'Software Components',
      },
      edit: {
        components_error: 'The software components for this hardware could not be loaded. Try again before saving.',
        no_components: 'This hardware has no software components.',
        save_failed: 'The release could not be saved',
      },
      fields: {
        custom: 'Custom',
        hardware_id: 'Hardware',
        hardware_revision_id: 'Hardware revision',
        name: 'Name',
        software_components: 'Software components',
      },
      expand: {
        label: 'Release components',
      },
    },
    subscriptions: {
      name: 'Subscription |||| Subscriptions',
      fields: {
        unlock_key: 'Unlock code',
      },
    },
    units: {
      name: 'Tovertafel |||| Tovertafels',
      forcedCaseName: 'Tovertafel |||| Tovertafels',
      list: {
        export_csv: 'Export CSV',
      },
      notifications: {
        designation_not_set: 'Designation is not set! Please set it first',
      },
      uses_staging_api:
        'This Tovertafel is set to use the Staging API. Changes made here will not work on the unit until it is switched back.',
      fields: {
        configuration_key: 'Configuration key',
        designation_id: 'Designation',
        developer_options: {
          allow_terminal: 'Allow terminal',
          api_status: 'API status',
          no_getconfig: 'Disable Get Config',
          debug_panel: 'Debug panel',
          design_tools: 'Design tools',
          diagnostics: 'Diagnostics',
          is_lte_enabled: 'LTE enabled',
          staging_api: 'Use staging API',
          boot_next_recovery: 'Boot next recovery',
          mqtt_enabled: 'MQTT enabled',
        },
        firmware_version: 'Firmware version',
        hardware_id: 'Hardware',
        hostname: 'Hostname',
        install_date: 'Installation date',
        language_ids: 'Additional languages',
        last_edit: 'Last edit',
        last_online: 'Last online',
        locale_id: 'Locale',
        mobile_network: {
          alert_threshold: 'Alert threshold',
          bandwidth_usage: 'Bandwidth usage',
          bandwidth_limit: 'Bandwidth limit',
          is_lte_enabled: 'LTE enabled',
          next_reset_time: 'Next data bundle reset time',
          provider: 'Provider',
          sim_id: 'Simcard ID',
          state: 'State',
          states: {
            unavailable: 'Unavailable',
            available: 'Available',
            critical: 'Critical',
          },
        },
        software_versions: {
          tover_tafel: 'Software version',
          tover_firmware: 'Firmware version',
          tover_up: 'ToverUp',
          tover_tele: 'Telekinesis',
          tover_sysup: 'ToverSysup',
          tover_comms: 'ToverComms',
          tover_conjure: 'FAT',
        },
        manifest: {
          current_release_name: 'Current release',
          new_release_name: 'New release',
          update_channel: 'Update channel',
          update_state: 'Update state',
          last_update_check_at: 'Last update check',
          last_update_received_at: 'Last update received',
        },
        causer_id: 'Causer',
        partner_group_id: 'Partner group',
        partner_id: 'Partner',
        real_ip: 'Last public IP (WAN)',
        serial: 'Serial number',
        show_uptime: 'Show cumulative uptime',
        software_version: 'Software version',
        studio_access: 'Tover Lab enabled',
        update_channel_id: 'Update channel',
      },
      section: {
        access_control: 'Access control',
        app_collections: 'Collections',
        details: 'System details',
        general: 'General',
        history: 'History',
        legal: 'Legal',
        localization: 'Localization',
        partner: 'Partner',
        preferences: 'Preferences',
        purchased_apps: 'Purchased apps',
        subscriptions: 'Subscriptions',
        trial_app_collections: 'Trial collections',
        trial_apps: 'Trial apps',
        simcard_info: 'Simcard info',
        mobile_permissions: 'Permissions',
        simcard_data: 'Data usage',
        software_update: 'Software update',
      },
      updater: {
        never_reported: 'This Tovertafel has not reported an update state yet.',
        no_state: 'Unknown',
        no_components: 'No software components reported.',
        fields: {
          component: 'Component',
          version: 'Installed version',
          updating_to: 'Updating to',
          update_state: 'Update state',
        },
      },
      live_logs: {
        source_tabs_label: 'Log source',
        sources: {
          nginx: 'Nginx',
          usagelog: 'Usage log',
          eventlog: 'Event log',
        },
        range: {
          label: 'Time range',
          options: {
            '15m': 'Last 15 minutes',
            '30m': 'Last 30 minutes',
            '1h': 'Last hour',
            '3h': 'Last 3 hours',
            '24h': 'Last 24 hours',
            '72h': 'Last 72 hours',
            '7d': 'Last 7 days',
            '30d': 'Last 30 days',
          },
        },
        auto_refresh: 'Auto-refresh every %{seconds} s',
        hint: {
          updated: 'Updated %{time}',
          auto_refresh: 'Auto-refresh on · updated %{time}',
          paused: 'Auto-refresh paused (page 1 only) · updated %{time}',
        },
        capped: 'Only the newest %{count} entries can be paged.',
        empty: 'No entries in this time range. Try a longer range.',
        unavailable: {
          title: 'Metrics unavailable',
          body: "The log store can't be reached. Try again in a few minutes.",
        },
        error: "Couldn't load entries (HTTP %{status}). Try again.",
        error_unknown: "Couldn't load entries. Try again.",
        try_again: 'Try again',
        running: {
          title: 'Running now',
          started: 'Started %{time}',
          elapsed: '%{duration} so far',
          none: 'No game running right now.',
          chip: 'Running',
        },
        datasets: {
          access: 'Access',
          error: 'Error',
        },
        expand: {
          label: 'Log entry fields',
          copied: 'Fields copied to the clipboard',
          copy_failed: "Couldn't copy to the clipboard",
        },
        columns: {
          time: 'Time',
          type: 'Type',
          status: 'Status',
          request: 'Request / message',
          size: 'Size',
          game: 'Game',
          start: 'Start',
          end: 'End',
          duration: 'Duration',
          version: 'Version',
          event: 'Event',
          message: 'Message',
        },
      },
      tabs: {
        info: 'Information',
        apps: 'Apps',
        configuration: 'Configuration',
        commands: 'Commands',
        developer_options: 'Developer options',
        diagnostics: 'Diagnostics',
        mobile_network: 'Mobile Network',
        activity_log: 'Activity log',
        live_logs: 'Live logs',
        reports: 'Reports',
      },
      count: '%{smart_count} Tovertafel |||| %{smart_count} Tovertafels',
      helpers: {
        hostname: 'Hostname is auto-generated for Pixie units',
        real_ip: 'Last public internet (WAN) address the API saw this Tovertafel connect from. Not the VPN IP.',
      },
      show: {
        links: {
          rsh: 'RSH',
          terminal: 'Terminal',
          config: 'Config',
          sim: 'Simcard',
          refresh_config: 'Refresh config',
          fix_missing_sim: 'Fix missing SIM',
          fat: 'FAT',
        },
        vpn: {
          method_label: 'VPN method',
          method_mqtt: 'MQTT',
          method_direct: 'Direct VPN',
          method_switch_label: 'Use the MQTT VPN method',
          mqtt_unsupported_tt1: 'MQTT is not supported on TT1 units.',
          mqtt_disabled: 'MQTT is disabled for this unit.',
          mqtt_no_ssh_key: 'No valid SSH key registered for this unit.',
          direct_no_permission: 'You do not have permission to open a Direct VPN connection.',
          no_method_available: 'No VPN connection method is available.',
          connect: 'Connect to VPN',
          status: 'Status',
          disconnected: 'disconnected',
          ip_address: 'VPN IP',
          copy_command: 'Copy SSH command',
          command_copied: 'SSH command copied to clipboard',
          connect_failed: 'Could not open the VPN connection',
          disconnect_failed: 'Could not close the VPN connection',
          switch_method_failed: 'Could not switch the VPN method',
        },
      },
      reports: {
        eventlog: 'Event log',
        game_played: 'Game played',
        start_date: 'Start date',
        end_date: 'End date',
        period: 'Report period',
        download: {
          bulkButton: 'Download reports',
          eventlog: 'Download event log',
          game_played: 'Download game played',
        },
        types: {
          eventlog: 'Event log',
          game_played: 'Game played',
        },
      },
      bulk_assign: {
        button: 'Assign Property',
        dialog_title: 'Batch Assign Property to Units',
        field_label: 'Property to assign',
        field_placeholder: 'Select a property...',
        value_label: 'New value',
        value_placeholder: 'Select a value...',
        units_list_label: 'Selected units (%{count})',
        confirm_button: 'Assign to %{count} unit(s)',
        cancel_button: 'Cancel',
        close_button: 'Close',
        warning_mixed_hardware:
          'Warning: selected units have mixed hardware revisions. Update Channel may not be compatible with all units. Incompatible units will be skipped.',
        success_all: 'Successfully assigned %{count} unit(s)',
        success_partial: 'Assigned %{successCount} unit(s), %{failureCount} failed',
        error_generic: 'Batch assign failed',
        results_success_heading: 'Successfully assigned (%{count})',
        results_failure_heading: 'Failed (%{count})',
        fields: {
          designation_id: 'Designation',
          locale_id: 'Locale',
          update_channel_id: 'Update Channel',
          partner_id: 'Partner',
        },
      },
      batch_assign_channel: {
        button: 'Assign units to channel',
        dialog_title: 'Batch assign units to update channel',
        channel_label: 'Update channel',
        tab_serials: 'Enter serials',
        tab_csv: 'Upload CSV',
        serials_label: 'Serial numbers',
        serials_placeholder: 'One serial number per line…',
        file_label: 'Choose CSV file',
        file_helper: 'Upload a .csv file with a serial number column',
        submit_button: 'Assign',
        cancel_button: 'Cancel',
        close_button: 'Close',
        success_all: 'Successfully assigned %{count} unit(s) to channel',
        error_generic: 'Batch assign to channel failed',
        results_summary: '%{updated} updated, %{skipped} skipped, %{not_found} not found (total: %{total})',
        results_updated_heading: 'Updated (%{count})',
        results_skipped_heading: 'Skipped (%{count})',
        results_not_found_heading: 'Not found (%{count})',
        warning_mixed_hardware:
          'Selected units have different hardware revisions. Only units with the same hardware revision can be batch-assigned to a channel. Please adjust your selection.',
        info_multiple_source_channels:
          'The selected units are currently on different update channels. You can optionally filter which source channels to move from, or leave all selected to move from all channels.',
        source_channel_label: 'Move from channel(s)',
        source_channel_all: 'All channels',
        source_channel_unassigned: 'Unassigned',
        source_channel_filtered_count: '%{count} of %{total} serials will be submitted',
        skip_reason_no_configuration_found: 'No configuration found',
        skip_reason_hardware_revision_mismatch: 'Hardware revision mismatch',
        skip_reason_hardware_revision_mismatch_details:
          'Hardware revision mismatch (%{hardware} / %{hardware_revision})',
      },
    },
    dashboard: {
      name: 'Dashboard',
      missing_units_from_elastic: 'Missing unit from ES',
      add_missing_units: 'Add missing units',
      fields: {},
    },
    channel_releases: {
      name: 'Channel release |||| Channel releases',
      fields: {
        created_at: 'Date added',
        hardware_id: 'Hardware',
        released_at: 'Release time',
        release_now: 'Release now',
        software_release_id: 'Software release',
        update_channel_id: 'Update channel',
      },
      forms: {
        edit: {
          header: 'You can edit only the release date here.',
        },
        create: {
          header: 'Please select the software release for this channel and set the release date.',
        },
      },
      action: {
        released: 'Released',
      },
    },
    update_channels: {
      name: 'Update channel |||| Update channels',
      fields: {
        name: 'Name',
        hardware_id: 'Hardware',
        release_now: 'Release now',
        released_at: 'Release time',
        software_release_id: 'Software release',
        short_name: 'Short name',
        hardware_revision: {
          name: 'Revision',
        },
        units_count: 'Units',
        current_releases: 'Current release',
      },
      helpers: {
        name_unique: 'Must be unique across all update channels.',
      },
      list: {
        not_released: 'Not released yet',
        revision_count: '%{smart_count} revision |||| %{smart_count} revisions',
      },
      section: {
        general: 'General',
        hardware_revisions: 'Hardware & revisions',
        current_release: 'Current release',
        release_history: 'Release history',
        units: 'Units',
      },
      release_history: {
        select_prompt: 'Select a hardware or revision to see its release history',
      },
      units: {
        empty: 'No units are assigned to this update channel.',
        search_placeholder: 'Search units…',
        assign_button: 'Assign to this channel',
        detach_button: 'Move to another channel',
        detach_confirm_title: 'Move units to another channel',
        detach_confirm_body: 'Select the target channel for %{count} unit(s):',
        detach_confirm_button: 'Move',
        detach_cancel_button: 'Cancel',
        detach_close_button: 'Close',
        detach_target_channel_label: 'Target channel',
        detach_success_all: 'Moved %{count} unit(s) to %{channel}',
        detach_success_partial: 'Moved %{successCount} unit(s) to %{channel}, %{failureCount} failed',
        detach_error: 'Move failed',
        results_success_heading: 'Successfully moved (%{count})',
        results_failure_heading: 'Failed (%{count})',
      },
      csv_import: {
        button: 'Import units from CSV',
        dialog_title: 'Import units from CSV',
        file_label: 'CSV file (serial numbers, one per line)',
        file_helper: 'Upload a .csv file with one unit serial number per line',
        submit_button: 'Import',
        cancel_button: 'Cancel',
        close_button: 'Close',
        success: 'Successfully imported %{count} unit(s)',
        success_partial: 'Imported %{successCount} unit(s), %{failureCount} failed',
        error: 'Import failed',
        results_success_heading: 'Imported (%{count})',
        results_failure_heading: 'Failed (%{count})',
      },
    },
    users: {
      name: 'User |||| Users',
      fields: {
        name: 'Name',
        email: 'Email',
        roles: 'Roles',
      },
    },
    vpn: {
      name: 'VPN Connection |||| VPN Connections',
      fields: {
        id: 'ID',
        ip_address: 'IP Address',
        created_at: 'Started',
      },
    },
    permissions: {
      name: 'Permission |||| Permissions',
      fields: {
        name: 'Name',
      },
      values: {
        'add_units': 'Add units',
        'dev_options': 'Developer options',
        'filter': 'Filter',
        'manage_apps': 'Manage apps',
        'manage_builds': 'Manage builds',
        'manage_collections': 'Manage collections',
        'manage_designations': 'Manage designations',
        'manage_flavours': 'Manage flavours',
        'manage_locales': 'Manage locales',
        'manage_location_contact': 'Manage location contact',
        'manage_units': 'Manage units',
        'manage_userroles': 'Manage user roles',
        'manage_users': 'Manage users',
        'mytover invite admin': 'MyTover invite admin',
        'mytover invite user': 'MyTover invite user',
        'offer_trial_apps': 'Offer trial apps',
        'perform_rollback': 'Perform rollback',
        'remove_units': 'Remove units',
        'report': 'Report',
        'reset_units': 'Reset units',
        'view_dashboard': 'View dashboard',
        'use_toverview': 'Use Toverview',
      },
    },
  },
};
